import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";

/** Where the engine takes a numeric setting's value from (dp D13). */
export type EffectiveSource =
  | "connector"
  | "platform default"
  | "platform default (forced)"
  | "legacy key"
  | "code default";

export const FORCED_SOURCE: EffectiveSource = "platform default (forced)";

export type PropertyDefinition = {
  name: string;
  display_name: string;
  required: boolean;
  field_type: string;
  placeholder: string;
  help_text: string;
  group: string;
  admin_only: boolean;
  // Only an org owner, org admin or platform admin may change it (the dp API
  // refuses a changed value from anyone else). Distinct from admin_only, which
  // means PLATFORM admin. Absent on older dp builds; treat as false.
  org_admin_only?: boolean;
  // Present for field_type === "select" (dropdown values) and
  // field_type === "multiselect" (tick boxes; stored joined with ";").
  options?: string[] | null;
  // Optional value -> plain-English label for select options (display only;
  // the saved value is always the raw option code).
  option_labels?: Record<string, string> | null;
  // Inclusive [min, max] for a whole-number field; blank is always allowed.
  numeric_range?: [number, number] | null;
  // Present on the connector-scoped endpoint: which registry the definition
  // came from ("source" | "target"). Absent on the type-scoped endpoint.
  role?: "source" | "target";
  // Connector-scoped endpoint only, and only for settings a platform default
  // can feed. Absent on older dp builds and on the type-scoped endpoint.
  effective_value?: string | null;
  effective_source?: EffectiveSource | null;
  recommended_value?: string | null;
};

// Cache definitions per connector type to avoid refetching.
//
// Deliberately NOT shared with the target-role cache below. The two roles are
// different reads of the SAME type string — a type fetched as a source would
// otherwise answer a later target read out of cache with the wrong registry,
// and the failure would look like "this target needs no credentials", which is
// precisely the dead end the target read exists to fix.
const definitionsCache: Record<string, PropertyDefinition[]> = {};
const targetDefinitionsCache: Record<string, PropertyDefinition[]> = {};

/**
 * SOURCE-role property definitions for a connector TYPE.
 *
 * Reports `failed` on a failed read and does NOT quietly degrade to an empty
 * list, for the same reason as `useTargetTypePropertyDefinitions` below: on the
 * connector CREATE form an empty list is not benign. It renders NO fields,
 * validation finds nothing to enforce, and the create POST comes back 422 with
 * nowhere to type the credential — and because the form treats "no definitions"
 * as "this type needs none", it shows a reassuring "What happens next?" panel
 * while doing it. The user sees a complete, correct-looking form, submits it,
 * and gets a failure they cannot act on.
 *
 * The caller MUST surface `failed`. Returning it is not enough — an ignored flag
 * reinstates the dead end.
 */
export function useConnectorPropertyDefinitions(connectorType: string) {
  const { token } = useAuth();
  const [definitions, setDefinitions] = useState<PropertyDefinition[]>(
    definitionsCache[connectorType] ?? [],
  );
  const [loading, setLoading] = useState(
    !definitionsCache[connectorType] && !!connectorType,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!connectorType || !token) return;

    // Use cache if available
    if (definitionsCache[connectorType]) {
      setDefinitions(definitionsCache[connectorType]);
      setLoading(false);
      setFailed(false);
      return;
    }

    // `cancelled` is load-bearing now that a failure is sticky state rather
    // than an empty list. Without it, switching type A -> B while A's request
    // is still in flight lets A's rejection set `failed` AFTER B has already
    // resolved, blocking submit on a form whose fields loaded perfectly well.
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    portalFetch(
      `/api/v1/gateway/business2cloud/api/v1/connectors/types/${encodeURIComponent(connectorType)}/property-definitions`,
      { token: token ?? undefined },
    )
      .then((data) => {
        if (cancelled) return;
        const defs = (data as PropertyDefinition[]) ?? [];
        // Only a SUCCESSFUL read is cached, so a transient failure does not
        // pin an empty list for the rest of the session.
        definitionsCache[connectorType] = defs;
        setDefinitions(defs);
      })
      .catch(() => {
        if (cancelled) return;
        setDefinitions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectorType, token]);

  return { definitions, loading, failed };
}

/**
 * TARGET-role property definitions for a connector TYPE.
 *
 * The connector CREATE form needs these and cannot use the connector-scoped
 * hook below, because at create time there is no connector id yet. Without it
 * the form had no way to learn that a SQL -> Asana connector must carry a
 * personal access token: it rendered no field, and the create POST came back
 * 422 with nowhere to type the credential.
 *
 * Backed by `?role=target` on the type-scoped endpoint. Fetches only while
 * `enabled` (i.e. once a target type is actually chosen).
 *
 * A failed read reports `failed`, and does NOT quietly degrade to an empty list
 * the way `useConnectorScopedPropertyDefinitions` below does. For that one an
 * empty list is benign — the edit sheet falls back to raw name/value rows. Here
 * there is no fallback: an empty list renders NO fields, validation finds
 * nothing to enforce, and the create POST comes back 422 with nowhere to type
 * the credential. That is exactly the dead end this hook exists to remove, so
 * degrading silently would reinstate it for the duration of any outage of that
 * endpoint. The caller must surface it.
 *
 * The SOURCE-role hook above reports `failed` for the same reason, on the same
 * form. Both halves of the create form are now guarded; only the connector-
 * scoped edit-sheet hook still degrades, and for it that is correct.
 */
export function useTargetTypePropertyDefinitions(
  targetType: string | null | undefined,
  enabled: boolean,
) {
  const { token } = useAuth();
  const [definitions, setDefinitions] = useState<PropertyDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !targetType || !token) {
      setDefinitions([]);
      setLoading(false);
      setFailed(false);
      return;
    }
    const cached = targetDefinitionsCache[targetType];
    if (cached) {
      setDefinitions(cached);
      setLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    portalFetch(
      `/api/v1/gateway/business2cloud/api/v1/connectors/types/${encodeURIComponent(targetType)}/property-definitions?role=target`,
      { token: token ?? undefined },
    )
      .then((data) => {
        if (cancelled) return;
        const defs = (data as PropertyDefinition[]) ?? [];
        targetDefinitionsCache[targetType] = defs;
        setDefinitions(defs);
      })
      .catch(() => {
        if (cancelled) return;
        setDefinitions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetType, enabled, token]);

  return { definitions, loading, failed };
}

/**
 * Connector-scoped property definitions: resolves this connector's source +
 * target types on the backend and returns the MERGED definitions, each stamped
 * with a `role`. Unlike the type-scoped hook above this surfaces TARGET-side
 * settings (e.g. Xero invoice status/type) that describe how the destination
 * is written. Fetches only while `enabled` (the edit sheet is open). On any
 * error it degrades to an empty list so the edit sheet falls back to its
 * pre-existing raw name/value rendering.
 *
 * `refetch()` reads them again, e.g. after a save that keeps the sheet open,
 * so a server-computed `effective_value` does not go stale. A failed refetch
 * keeps the definitions already shown rather than collapsing the form.
 */
export function useConnectorScopedPropertyDefinitions(
  connectorId: number | null | undefined,
  enabled: boolean,
) {
  const { token } = useAuth();
  const [definitions, setDefinitions] = useState<PropertyDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Set by refetch(): tells the next read it is a refetch.
  const isRefetch = useRef(false);

  const refetch = useCallback(() => {
    isRefetch.current = true;
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const refetching = isRefetch.current;
    isRefetch.current = false;
    if (!enabled || !connectorId || !token) {
      setDefinitions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    portalFetch(
      `/api/v1/gateway/business2cloud/api/v1/connectors/${connectorId}/property-definitions`,
      { token: token ?? undefined },
    )
      .then((data) => {
        if (cancelled) return;
        setDefinitions((data as PropertyDefinition[]) ?? []);
      })
      .catch(() => {
        if (!cancelled && !refetching) setDefinitions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectorId, enabled, token, reloadKey]);

  return { definitions, loading, refetch };
}
